/**
 * Parser RTF robusto para reportes XLTEK/IMSS.
 * Preserva celdas/filas de tablas y decodifica cp1252 (acentos españoles).
 */

export interface RobustRtfTable {
  rows: string[][];
}

export interface RobustRtfParseResult {
  plainText: string;
  tables: RobustRtfTable[];
  warnings: string[];
}

/** Caracteres cp1252 en rango 0x80-0x9F que difieren de Latin-1 */
const CP1252_SPECIAL: Record<number, string> = {
  0x80: '€', 0x82: '‚', 0x83: 'ƒ', 0x84: '„', 0x85: '…', 0x86: '†', 0x87: '‡',
  0x88: 'ˆ', 0x89: '‰', 0x8a: 'Š', 0x8b: '‹', 0x8c: 'Œ', 0x8e: 'Ž',
  0x91: '‘', 0x92: '’', 0x93: '"', 0x94: '"', 0x95: '•', 0x96: '–', 0x97: '—',
  0x98: '˜', 0x99: '™', 0x9a: 'š', 0x9b: '›', 0x9c: 'œ', 0x9e: 'ž', 0x9f: 'Ÿ'
};

function decodeCp1252Byte(byte: number): string {
  if (byte >= 0x80 && byte <= 0x9F && CP1252_SPECIAL[byte]) {
    return CP1252_SPECIAL[byte];
  }
  return String.fromCharCode(byte);
}

function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function normalizeLines(text: string): string {
  return text
    .split(/\r?\n/)
    .map(line => normalizeWhitespace(line))
    .filter(Boolean)
    .join('\n');
}

const SKIP_COMMANDS = new Set([
  'fonttbl', 'colortbl', 'stylesheet', 'info', 'filetbl', 'listtable', 'revtbl',
  'mmathpr', 'xmlnstbl', 'themedata', 'colorschememapping', 'pict', 'shp', 'nonshppict', 'objdata', 'blipuid'
]);
const SKIP_GROUP_START_PATTERN = /^\\(?:fonttbl|colortbl|stylesheet|info|filetbl|listtable|revtbl|pnseclvl\d|pict|shp|object)/i;
const SKIP_DEST_PATTERN = /^\\\*\\(?:generator|defchp|defpap|rsidtbl|wgrffmtfilter|panose)/i;

export function parseRobustRtf(rtf: string): RobustRtfParseResult {
  const warnings: string[] = [];
  const tables: RobustRtfTable[] = [];
  let currentTableRows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let plainParts: string[] = [];
  const skipStack: boolean[] = [];
  let skipNextGroup = false;
  let skipInlineMetadata = false;

  let i = 0;
  const len = rtf.length;

  const isSkipping = () =>
    skipInlineMetadata || (skipStack.length > 0 && skipStack[skipStack.length - 1]);

  const shouldSkipGroupAt = (pos: number): boolean => {
    if (skipNextGroup) {
      skipNextGroup = false;
      return true;
    }
    const peek = rtf.slice(pos, pos + 80).replace(/^\s+/, '');
    return SKIP_GROUP_START_PATTERN.test(peek) || SKIP_DEST_PATTERN.test(peek);
  };

  const appendText = (text: string) => {
    if (!isSkipping()) {
      currentCell += text;
      plainParts.push(text);
    }
  };

  const flushCell = () => {
    const text = normalizeWhitespace(currentCell);
    currentRow.push(text);
    currentCell = '';
  };

  const flushRow = () => {
    if (currentRow.length > 0) {
      currentTableRows.push([...currentRow]);
      if (!isSkipping()) plainParts.push('\n');
    }
    currentRow = [];
  };

  const flushTable = () => {
    if (currentTableRows.length >= 1 && currentTableRows.some(r => r.length >= 2)) {
      tables.push({ rows: [...currentTableRows] });
    }
    currentTableRows = [];
  };

  while (i < len) {
    const ch = rtf[i];

    if (ch === '{') {
      const parentSkip = skipStack.length > 0 && skipStack[skipStack.length - 1];
      skipStack.push(parentSkip || shouldSkipGroupAt(i + 1));
      i++;
      continue;
    }

    if (ch === '}') {
      if (skipStack.length > 0) skipStack.pop();
      i++;
      continue;
    }

    if (ch === '\\') {
      i++;
      if (i >= len) break;

      const next = rtf[i];

      // Hex char cp1252: \'XX
      if (next === "'") {
        i++;
        const hex = rtf.slice(i, i + 2);
        if (/^[0-9a-fA-F]{2}$/.test(hex)) {
          const char = decodeCp1252Byte(parseInt(hex, 16));
          appendText(char);
          i += 2;
        }
        continue;
      }

      // Unicode: \uN?  (optional replacement char after)
      if (next === 'u' || next === 'U') {
        i++;
        let sign = 1;
        if (rtf[i] === '-') {
          sign = -1;
          i++;
        }
        let numStr = '';
        while (i < len && /\d/.test(rtf[i])) {
          numStr += rtf[i];
          i++;
        }
        if (numStr) {
          let code = sign * parseInt(numStr, 10);
          if (code < 0) code = 65536 + code;
          const char = String.fromCodePoint(code);
          appendText(char);
        }
        // Skip optional replacement character
        if (i < len && rtf[i] === '?') i++;
        if (i < len && rtf[i] === ' ') i++;
        continue;
      }

      // Read alphabetic command name
      let cmd = '';
      while (i < len && /[a-zA-Z]/.test(rtf[i])) {
        cmd += rtf[i];
        i++;
      }

      // Read optional numeric parameter (may be negative)
      let param = '';
      if (i < len && rtf[i] === '-') {
        param += rtf[i];
        i++;
      }
      while (i < len && /\d/.test(rtf[i])) {
        param += rtf[i];
        i++;
      }

      // Skip delimiter space after command
      if (i < len && rtf[i] === ' ') i++;

      if (SKIP_COMMANDS.has(cmd.toLowerCase())) {
        if (cmd.toLowerCase() === 'colortbl') {
          skipInlineMetadata = true;
        } else {
          skipNextGroup = true;
        }
      }

      if (skipInlineMetadata && /^(paperw|widowctrl|defchp|defpap|noqfpromote|stylesheet)$/i.test(cmd)) {
        skipInlineMetadata = false;
      }

      // Destinations like \*\generator are skipped via the following group
      if (cmd === '*' && i < len && rtf[i] === '\\') {
        const destPeek = rtf.slice(i, i + 40);
        if (/^\\(?:generator|defchp|defpap|rsidtbl|wgrffmtfilter)/i.test(destPeek)) {
          skipNextGroup = true;
        }
      }

      if (cmd === 'par' || cmd === 'line') {
        if (currentCell) flushCell();
        if (!isSkipping()) plainParts.push('\n');
        currentCell = '';
      } else if (cmd === 'tab') {
        if (currentCell) flushCell();
        if (!isSkipping()) plainParts.push('\t');
      } else if (cmd === 'cell') {
        flushCell();
        if (!isSkipping()) plainParts.push('\t');
      } else if (cmd === 'row') {
        flushRow();
        if (!isSkipping()) plainParts.push('\n');
      } else if (cmd === 'trowd') {
        // inicio de definición de fila de tabla
      } else if (cmd === 'bin') {
        // Skip binary data
        const binLen = param ? parseInt(param, 10) : 0;
        i += binLen;
      }
      // All other commands (font, color, etc.) are ignored
      continue;
    }

    // Regular character
    appendText(ch);
    i++;
  }

  if (currentCell) flushCell();
  if (currentRow.length) flushRow();
  if (currentTableRows.length) flushTable();

  let plainText = plainParts.join('');
  plainText = plainText
    .replace(/\t+/g, '\t')
    .replace(/\n{3,}/g, '\n\n');
  plainText = normalizeLines(plainText);

  if (plainText.length < 20) {
    warnings.push('Texto RTF extraído muy corto; el archivo puede estar corrupto o vacío.');
  }

  if (tables.length === 0) {
    warnings.push('No se detectaron tablas RTF estructuradas; se usará texto plano.');
  }

  return { plainText, tables, warnings };
}

/**
 * Convierte tablas RTF a formato ExtractedTable-compatible (rows de celdas).
 */
export function rtfTablesToExtractedRows(tables: RobustRtfTable[]): string[][][] {
  return tables.map(table => table.rows);
}
